import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import Checkbox from 'core/elements/input/Checkbox'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import GridTableHeading from './GridTableHeading'
import { GridManagedColumnsProps } from './hooks/useGridManagedColumns'
import { GridBatchActionsProps, SelectableParsedGridRow } from './hooks/useGridSelectableRows'
import { GridSortingProps } from './hooks/useGridSorting'

export interface GridHeaderProps<T>
  extends GridSortingProps,
    GridBatchActionsProps<T>,
    GridManagedColumnsProps {
  pageRows: Array<SelectableParsedGridRow<T>>
  rowMenuItemsLength: number
  hideCheckboxes: boolean
  isReadOnly?: boolean
}

interface SelectAllColumnProps<T> {
  className: string
  pageRows: Array<SelectableParsedGridRow<T>>
  rowsSelectionDisabled: boolean
  multiSelectionEnabled: boolean
  toggleSelectAll: (rows: Array<SelectableParsedGridRow<T>>) => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  gridHead: {
    border: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 9, // 1 more than the sticky row action
  },
  gridHeadTr: {
    textAlign: 'left',
  },
  gridHeadTitleTr: {
    backgroundColor: theme.components.table.hoverBackground,
    height: 36,
    border: 'none',
    borderTop: `1px solid ${theme.components.table.border}`,
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  gridSelectAllTh: {
    width: 40,
    paddingLeft: 10,
  },
}))

function SelectAllColumn<T>({
  className,
  pageRows,
  rowsSelectionDisabled,
  multiSelectionEnabled,
  toggleSelectAll,
}: SelectAllColumnProps<T>) {
  if (rowsSelectionDisabled) {
    return null
  }
  // Reflect only the CURRENT page. The box is checked when every row on this
  // page is selected, and indeterminate when some (but not all) are. Deriving
  // this from the global selection status made the box show a dash on pages
  // with nothing selected whenever another page had a selection. SELECTROWS-FR-016.
  const checked = pageRows.length > 0 && pageRows.every(({ isSelected }) => isSelected)
  const indeterminate = !checked && pageRows.some(({ isSelected }) => isSelected)
  return (
    <th className={clsx(className, 'select-column')}>
      {multiSelectionEnabled ? (
        <Checkbox
          checked={checked || indeterminate}
          indeterminate={indeterminate}
          onChange={() => toggleSelectAll(pageRows)}
        />
      ) : null}
    </th>
  )
}

export default function GridHeader<T>(props: GridHeaderProps<T>) {
  const {
    columns,
    sortingDisabled,
    sortedBy,
    sortedDirection,
    toggleSort,
    multiSelectionEnabled,
    toggleSelectAll,
    rowsSelectionDisabled,
    pageRows,
    rowMenuItemsLength,
    hideCheckboxes,
    isReadOnly,
  } = props
  const classes = useStyles()

  return (
    <thead className={classes.gridHead} data-testid="grid-header--thead-0">
      <Text
        component="tr"
        variant="caption2"
        className={clsx(classes.gridHeadTr, classes.gridHeadTitleTr)}
      >
        {!hideCheckboxes && (
          <SelectAllColumn
            className={classes.gridSelectAllTh}
            {...{
              pageRows,
              rowsSelectionDisabled,
              multiSelectionEnabled,
              toggleSelectAll,
            }}
          />
        )}
        {columns
          .filter(({ visible }) => visible !== false)
          .map(({ key, width, tooltip, label }) => (
            <GridTableHeading
              width={width}
              sortingDisabled={sortingDisabled}
              onClick={sortingDisabled ? null : () => toggleSort(key)}
              key={key}
            >
              <Text
                title={String(tooltip || label)}
                variant="caption2"
                className="grid_header-text"
              >
                {label}
              </Text>
              {!sortingDisabled && sortedBy === key ? (
                <FontAwesomeIcon aria-hidden="true" solid className="grid_header-direction">
                  {`sort-${sortedDirection === 'asc' ? 'up' : 'down'}`}
                </FontAwesomeIcon>
              ) : null}
            </GridTableHeading>
          ))}
        {isReadOnly ? null : rowMenuItemsLength ? (
          <GridTableHeading width={20} sortingDisabled key="row-menu" />
        ) : null}
      </Text>
    </thead>
  )
}
